import { useState, useEffect } from 'react'
import './styles.css'
import './mobile.css'
import { siteData } from './data'
import { BottomNav } from './BottomNav'

type MinutoPost = typeof siteData.minutoInfo.posts[number]

export const Index = () => {
    const [activeSection, setActiveSection] = useState('home')
    const [selectedPost, setSelectedPost] = useState<MinutoPost | null>(null)
    const [minutoVisible, setMinutoVisible] = useState(3)
    const [expandedTestimonials, setExpandedTestimonials] = useState<Set<number>>(new Set())

    const toggleTestimonial = (index: number) => {
        setExpandedTestimonials(prev => {
            const next = new Set(prev)
            if (next.has(index)) next.delete(index)
            else next.add(index)
            return next
        })
    }

    const scrollToSection = (sectionId: string) => {
        setActiveSection(sectionId)
        const element = document.getElementById(sectionId)
        if (element) {
            const headerHeight = 80
            const elementPosition = element.offsetTop - headerHeight
            window.scrollTo({
                top: elementPosition,
                behavior: 'smooth'
            })
        }
    }

    useEffect(() => {
        const handleScroll = () => {
            const sections = ['home', 'servicos', 'sobre', 'valores', 'depoimentos', 'materialPub', 'equipe', 'minutoInfo', 'faq']
            const headerHeight = 80
            const scrollPosition = window.scrollY + headerHeight + 50

            for (const sectionId of sections) {
                const element = document.getElementById(sectionId)
                if (element) {
                    const offsetTop = element.offsetTop
                    const offsetHeight = element.offsetHeight

                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(sectionId)
                        break
                    }
                }
            }

            const observerOptions = {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            }

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible')
                    }
                })
            }, observerOptions)

            sections.forEach(sectionId => {
                const element = document.getElementById(sectionId)
                if (element) {
                    observer.observe(element)
                }
            })

            return () => observer.disconnect()
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    return (
        <div className="app">
            <header className="header header-blur">
                <nav className="nav nav-centered">
                    <ul className="nav-menu nav-pill">
                        <li><button onClick={() => scrollToSection('home')} className={activeSection === 'home' ? 'active' : ''}>{siteData.menu.home}</button></li>
                        <li><button onClick={() => scrollToSection('servicos')} className={activeSection === 'servicos' ? 'active' : ''}>{siteData.menu.services}</button></li>
                        <li><button onClick={() => scrollToSection('sobre')} className={activeSection === 'sobre' ? 'active' : ''}>{siteData.menu.about}</button></li>
                        <li><button onClick={() => scrollToSection('valores')} className={activeSection === 'valores' ? 'active' : ''}>{siteData.menu.values}</button></li>
                        <li><button onClick={() => scrollToSection('depoimentos')} className={activeSection === 'depoimentos' ? 'active' : ''}>{siteData.menu.testimonials}</button></li>
                        <li><button onClick={() => scrollToSection('materialPub')} className={activeSection === 'materialPub' ? 'active' : ''}>{siteData.menu.materialPub}</button></li>
                        <li><button onClick={() => scrollToSection('equipe')} className={activeSection === 'equipe' ? 'active' : ''}>{siteData.menu.equipe}</button></li>
                        <li><button onClick={() => scrollToSection('minutoInfo')} className={activeSection === 'minutoInfo' ? 'active' : ''}>{siteData.menu.minutoInfo}</button></li>
                        <li><button onClick={() => scrollToSection('faq')} className={activeSection === 'faq' ? 'active' : ''}>{siteData.menu.faq}</button></li>
                    </ul>
                </nav>
            </header>

            <section id="home" className="hero-armando">
                <div className="hero-bg-split"></div>
                <div className="hero-content-armando">
                    <div className="hero-info-armando">
                        <span className="crp-pill">{siteData.hero.crp}</span>
                        <h1 className="hero-name-armando">{siteData.hero.title}</h1>
                        <a href={siteData.social.whatsapp.url} target="_blank" rel="noopener noreferrer" className="cta-button-armando" style={{ background: '#23406e' }}>{siteData.hero.ctaButton}</a>
                    </div>
                    <div className="hero-brain-wrap">
                        <img src="/cerebro.png" alt="Cérebro neuropsicológico" className="hero-brain-img" />
                    </div>
                </div>
            </section>

            <section id="servicos" className="services">
                <div className="container">
                    <h2>{siteData.services.title}</h2>
                    <div className="services-grid" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ fontSize: '1.15rem', lineHeight: '2', color: '#555', margin: 0, textAlign: 'center' }}>
                            {siteData.services.items.map((item, index) => (
                                <span key={index}>
                                    {item}<br />
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section id="sobre" className="about">
                <div className="container">
                    <h2>{siteData.about.title}</h2>
                    <div className="about-content">
                        {siteData.about.paragraphs.map((paragraph, index) => (
                            <p key={index}>{paragraph}</p>
                        ))}
                    </div>
                </div>
            </section>

            <section id="valores" className="values">
                <div className="container">
                    <h2>{siteData.values.title}</h2>
                    <div className="values-content">
                        <p>{siteData.values.content}</p>
                    </div>
                </div>
            </section>

            <section id="depoimentos" className="testimonials">
                <div className="container">
                    <h2>{siteData.testimonials.title}</h2>
                    <h3>{siteData.testimonials.subtitle}</h3>

                    <div className="testimonials-grid">
                        {siteData.testimonials.items.map((testimonial, index) => {
                            const isExpanded = expandedTestimonials.has(index)
                            return (
                                <div key={index} className="testimonial-card">
                                    <div className="testimonial-avatar"></div>
                                    <h4>{testimonial.name}</h4>
                                    <div className={`testimonial-text-wrap${isExpanded ? ' expanded' : ''}`}>
                                        <p>{testimonial.text}</p>
                                    </div>
                                    <button
                                        className="testimonial-vermais"
                                        onClick={() => toggleTestimonial(index)}
                                    >
                                        {isExpanded ? 'Ver menos ▲' : 'Ver mais ▼'}
                                    </button>
                                </div>
                            )
                        })}
                    </div>

                </div>
            </section>

            <section id="materialPub" className="material-pub">
                <div className="container">
                    <h2 className="section-title-pub">{siteData.materialPub.title}</h2>
                    <div className="pub-list">
                        {siteData.materialPub.items.map((item, index) => (
                            <div key={index} className="pub-card">
                                <span className="pub-type">{index + 1}. {item.type}</span>
                                <a href={item.url} target="_blank" rel="noopener noreferrer" className="pub-title">
                                    {item.title}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="equipe" className="equipe">
                <div className="container">
                    <h2 className="section-title-equipe">{siteData.equipe.title}</h2>
                    <div className="equipe-grid">
                        {siteData.equipe.members.map((member, index) => (
                            <div key={index} className="equipe-card">
                                <div className="equipe-photo-wrap">
                                    <img
                                        src={member.photo}
                                        alt={member.name}
                                        className="equipe-photo"
                                        onError={(e) => { e.currentTarget.src = member.fallback; }}
                                    />
                                </div>
                                <div className="equipe-name">{member.name}</div>
                                <div className="equipe-crp">{member.crp}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section id="minutoInfo" className="minuto-info">
                <div className="container">
                    <h2 className="section-title-minuto">{siteData.minutoInfo.title}</h2>
                    <div className="minuto-grid">
                        {siteData.minutoInfo.posts.slice(0, minutoVisible).map((post, index) => (
                            <article key={index} className="minuto-card" onClick={() => setSelectedPost(post)}>
                                <div className="minuto-card-img">
                                    <span className="minuto-card-icon">📋</span>
                                </div>
                                <div className="minuto-card-body">
                                    <h3 className="minuto-card-titulo">{post.titulo}</h3>
                                    <p className="minuto-card-descricao">{post.descricao}</p>
                                    <span className="minuto-card-lermais">Ler mais →</span>
                                </div>
                            </article>
                        ))}
                    </div>
                    {minutoVisible < siteData.minutoInfo.posts.length && (
                        <div className="minuto-mais-wrap">
                            <button
                                className="minuto-mais-btn"
                                onClick={() => setMinutoVisible(v => v + 3)}
                            >
                                Ver mais
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {selectedPost && (
                <div className="minuto-modal-overlay" onClick={() => setSelectedPost(null)}>
                    <div className="minuto-modal" onClick={e => e.stopPropagation()}>
                        <button className="minuto-modal-close" onClick={() => setSelectedPost(null)}>✕</button>
                        <h2 className="minuto-modal-titulo">{selectedPost.titulo}</h2>
                        <div className="minuto-modal-conteudo">
                            {selectedPost.conteudo.split('\n\n').map((paragrafo, i) => (
                                <p key={i}>{paragrafo}</p>
                            ))}
                        </div>
                        <div className="minuto-modal-autor">
                            <span>{selectedPost.autor}</span>
                            <span>{selectedPost.crp}</span>
                        </div>
                    </div>
                </div>
            )}

            <section id="faq" className="faq-nova">
                <h2 className="faq-titulo">{siteData.faq.title}</h2>
                <p className="faq-subtitulo">{siteData.faq.subtitle}</p>
                <div className="faq-lista">
                    {siteData.faq.items.map((item, idx) => (
                        <FaqItem key={idx} pergunta={item.pergunta} resposta={item.resposta} />
                    ))}
                </div>
            </section>

            <BottomNav activeSection={activeSection} onNavigate={scrollToSection} />


            <div className="floating-social-bar">
                <a href={siteData.social.whatsapp.url} className="social-icon" title={siteData.social.whatsapp.title} target="_blank" rel="noopener noreferrer" style={{ background: '#23406e' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M16.7 15.3c-.3-.2-1.7-.8-2-.9-.3-.1-.5-.2-.7.2-.2.3-.7 1-.9 1.2-.2.2-.4.2-.7.1-.3-.1-1.2-.4-2.2-1.3-.8-.7-1.3-1.5-1.5-1.8-.2-.3 0-.4.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2.1-.4 0-.6-.1-.2-.6-1.4-.8-1.9-.2-.5-.4-.4-.6-.4-.2 0-.4 0-.6 0-.2 0-.5.1-.7.4-.2.3-.9.9-.9 2.1 0 1.2.9 2.3 1 2.4.1.2 1.7 2.6 4.1 3.6.6.2 1 .3 1.4.4.6.1 1.1.1 1.5.1.5-.1 1.4-.6 1.6-1.1.2-.5.2-1 .1-1.1-.1-.1-.2-.2-.5-.3z" />
                    </svg>
                </a>
                <a href={siteData.social.email.url} className="social-icon" title={siteData.social.email.title} target="_blank" rel="noopener noreferrer" style={{ background: '#23406e' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="2" /><path d="M22 6l-10 7L2 6" /></svg>
                </a>
                <a href={siteData.social.instagram.url} className="social-icon" title={siteData.social.instagram.title} target="_blank" rel="noopener noreferrer" style={{ background: '#23406e' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.5" y2="6.5" /></svg>
                </a>
            </div>

        </div>
    )
}

function FaqItem({ pergunta, resposta }: { pergunta: string; resposta: string }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`faq-card${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
            <div className="faq-card-header">
                <span className="faq-icon">{open ? '–' : '+'}</span>
                <span className="faq-pergunta">{pergunta}</span>
            </div>
            {open && <div className="faq-resposta">{resposta}</div>}
        </div>
    );
}